import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

/**
 * Snake Game Component
 * Classico gioco in cui controlli un serpente che cresce mangiando il cibo
 */
const Snake = () => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('menu'); // menu, playing, paused, gameover
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(localStorage.getItem('snakeHighScore') || 0);
  const gameStateRef = useRef({
    snake: [{ x: 10, y: 10 }],
    food: { x: 15, y: 15 },
    direction: { x: 1, y: 0 },
    nextDirection: { x: 1, y: 0 },
    score: 0,
    gameOver: false
  });

  const GRID_SIZE = 20;
  const CANVAS_SIZE = 400;
  const CELL_SIZE = CANVAS_SIZE / GRID_SIZE;

  // Inizializzazione del gioco
  const initGame = () => {
    gameStateRef.current = {
      snake: [{ x: 10, y: 10 }],
      food: { x: Math.floor(Math.random() * GRID_SIZE), y: Math.floor(Math.random() * GRID_SIZE) },
      direction: { x: 1, y: 0 },
      nextDirection: { x: 1, y: 0 },
      score: 0,
      gameOver: false
    };
    setScore(0);
    setGameState('playing');
  };

  // Funzione per disegnare il gioco
  const draw = (ctx) => {
    const game = gameStateRef.current;

    // Sfondo
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Griglia
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= GRID_SIZE; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL_SIZE, 0);
      ctx.lineTo(i * CELL_SIZE, CANVAS_SIZE);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, i * CELL_SIZE);
      ctx.lineTo(CANVAS_SIZE, i * CELL_SIZE);
      ctx.stroke();
    }

    // Disegna il serpente
    game.snake.forEach((segment, index) => {
      ctx.fillStyle = index === 0 ? '#00ff00' : '#00dd00';
      ctx.fillRect(
        segment.x * CELL_SIZE + 1,
        segment.y * CELL_SIZE + 1,
        CELL_SIZE - 2,
        CELL_SIZE - 2
      );
    });

    // Disegna il cibo
    ctx.fillStyle = '#ff4444';
    ctx.beginPath();
    ctx.arc(
      game.food.x * CELL_SIZE + CELL_SIZE / 2,
      game.food.y * CELL_SIZE + CELL_SIZE / 2,
      CELL_SIZE / 2 - 2,
      0,
      Math.PI * 2
    );
    ctx.fill();
  };

  // Funzione per aggiornare il gioco
  const updateGame = () => {
    const game = gameStateRef.current;

    if (game.gameOver) return;

    // Aggiorna la direzione
    game.direction = { ...game.nextDirection };

    // Calcola la nuova testa
    const head = game.snake[0];
    const newHead = {
      x: (head.x + game.direction.x + GRID_SIZE) % GRID_SIZE,
      y: (head.y + game.direction.y + GRID_SIZE) % GRID_SIZE
    };

    // Controlla collisione con il corpo
    if (game.snake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
      game.gameOver = true;
      setGameState('gameover');
      if (game.score > highScore) {
        setHighScore(game.score);
        localStorage.setItem('snakeHighScore', game.score);
      }
      return;
    }

    // Aggiungi la testa
    game.snake.unshift(newHead);

    // Controlla se ha mangiato il cibo
    if (newHead.x === game.food.x && newHead.y === game.food.y) {
      game.score += 10;
      setScore(game.score);
      // Genera nuovo cibo
      game.food = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE)
      };
    } else {
      // Rimuovi la coda
      game.snake.pop();
    }
  };

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const gameLoop = setInterval(() => {
      updateGame();
      draw(ctx);
    }, 100);

    return () => clearInterval(gameLoop);
  }, [gameState]);

  // Disegna iniziale
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (gameState === 'menu') {
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      ctx.fillStyle = '#fff';
      ctx.font = '20px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Premi Play per iniziare', CANVAS_SIZE / 2, CANVAS_SIZE / 2);
    } else if (gameState === 'gameover') {
      draw(ctx);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Game Over!', CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 20);
      ctx.font = '16px Arial';
      ctx.fillText(`Score: ${score}`, CANVAS_SIZE / 2, CANVAS_SIZE / 2 + 20);
    } else {
      draw(ctx);
    }
  }, [gameState, score]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      const game = gameStateRef.current;
      switch (e.key.toLowerCase()) {
        case 'arrowup':
        case 'w':
          if (game.direction.y === 0) game.nextDirection = { x: 0, y: -1 };
          e.preventDefault();
          break;
        case 'arrowdown':
        case 's':
          if (game.direction.y === 0) game.nextDirection = { x: 0, y: 1 };
          e.preventDefault();
          break;
        case 'arrowleft':
        case 'a':
          if (game.direction.x === 0) game.nextDirection = { x: -1, y: 0 };
          e.preventDefault();
          break;
        case 'arrowright':
        case 'd':
          if (game.direction.x === 0) game.nextDirection = { x: 1, y: 0 };
          e.preventDefault();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-lg-6">
          <div className="card shadow-lg">
            <div className="card-header bg-success text-white">
              <h3 className="mb-0">🐍 Snake Game</h3>
            </div>
            <div className="card-body text-center">
              <canvas
                ref={canvasRef}
                width={CANVAS_SIZE}
                height={CANVAS_SIZE}
                style={{
                  border: '2px solid #333',
                  borderRadius: '8px',
                  backgroundColor: '#000',
                  display: 'block',
                  margin: '0 auto 1rem'
                }}
              />

              {/* Score Display */}
              <div className="row mb-3">
                <div className="col-6">
                  <h5 className="text-muted">Score</h5>
                  <h2 className="text-success">{score}</h2>
                </div>
                <div className="col-6">
                  <h5 className="text-muted">High Score</h5>
                  <h2 className="text-primary">{highScore}</h2>
                </div>
              </div>

              {/* Controls */}
              <div className="d-flex gap-2 justify-content-center mb-3">
                {gameState === 'menu' && (
                  <button
                    onClick={initGame}
                    className="btn btn-success btn-lg"
                    aria-label="Start game"
                  >
                    <Play size={20} className="me-2" />
                    Inizia
                  </button>
                )}
                {gameState === 'playing' && (
                  <button
                    onClick={() => setGameState('paused')}
                    className="btn btn-warning btn-lg"
                    aria-label="Pause game"
                  >
                    <Pause size={20} className="me-2" />
                    Pausa
                  </button>
                )}
                {gameState === 'paused' && (
                  <button
                    onClick={() => setGameState('playing')}
                    className="btn btn-success btn-lg"
                    aria-label="Resume game"
                  >
                    <Play size={20} className="me-2" />
                    Riprendi
                  </button>
                )}
                {gameState === 'gameover' && (
                  <button
                    onClick={initGame}
                    className="btn btn-primary btn-lg"
                    aria-label="Play again"
                  >
                    <RotateCcw size={20} className="me-2" />
                    Gioca di Nuovo
                  </button>
                )}
              </div>

              {/* Instructions */}
              <div className="alert alert-info" role="region" aria-label="Game instructions">
                <strong>Come Giocare:</strong>
                <ul className="mb-0 mt-2 text-start">
                  <li>Usa i tasti <kbd>↑↓←→</kbd> per muovere il serpente</li>
                  <li>Mangia il cibo rosso per crescere</li>
                  <li>Evita di colpire i muri o te stesso</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Snake;
